/**
 * The hero film served from this site, played by the browser's own decoder.
 *
 * <p>There is no player here — the element is a `video` tag and the bytes on the wire are the film
 * itself. That is the whole difference from the embedded delivery: no third-party script, no
 * third-party cookies, no handshake to find out whether a picture has appeared, and no chrome to hide. Measured
 * against the embedded player it saves about 1.1 MB of code over the wire and ten requests to a
 * third party, before a single frame of film.</p>
 *
 * <p><b>The layer is revealed before playback is asked for, and that order is not a preference.</b>
 * Chrome pauses video-only media that is not being displayed, to save power — measured here as
 * `AbortError: video-only background media was paused to save power`, with the film fully
 * downloaded and sitting at frame zero. Asking a hidden element to play is therefore asking for
 * nothing. It can be revealed safely because, unlike an embedded player, a video element with a
 * decoded first frame is already showing a picture.</p>
 *
 * <p><b>The same rule bites again after the film has started, and that is what the observer is
 * for.</b> Chrome pauses a film whose hero has scrolled away or been covered — the consent dialog
 * covering it was enough — and it never starts it again by itself. Measured: the film stopped at
 * the loop's first frame and stayed there. So playback is not something asked for once; it follows
 * whether the hero is actually on screen, which is what a visitor means by a film that is playing,
 * and which spends no battery on a picture nobody is looking at.</p>
 *
 * <p>The film is encoded as a seamless loop — its last second is dissolved onto its first — so
 * `loop` is enough and no work is needed at the seam.</p>
 */
import type { HeroFilmPlayer, HeroFilmSignals } from "./hero-film-player";

/**
 * Builds a player for a film this site serves.
 *
 * @param source A site-relative address. The Infrastructure provider rejects everything else, so a
 *   third-party address cannot arrive here through the editor's field.
 * @param type The media type, so the browser can refuse a container it cannot decode without first
 *   downloading it.
 */
export function selfHostedHeroFilm(source: string, type: string): HeroFilmPlayer {
  const video = document.createElement("video");

  // Autoplay is refused with sound in every browser. The attribute is set as well as the property:
  // the property is what the autoplay policy reads, and older Safari read the attribute.
  video.muted = true;
  video.defaultMuted = true;
  video.setAttribute("muted", "");

  video.loop = true;
  video.playsInline = true;
  video.disablePictureInPicture = true;

  // Nothing at all is fetched until start() adds the source. The gate has not decided yet.
  video.preload = "none";

  // The photograph underneath carries the alternative text, and the film adds nothing a screen
  // reader or a keyboard could use. The stop button is the one control, and it is a sibling.
  video.tabIndex = -1;
  video.setAttribute("aria-hidden", "true");

  let signals: HeroFilmSignals | null = null;
  let onScreen = false;

  /** True while an unasked-for pause is being answered, so it is answered once and not fought over. */
  let resuming = false;

  /**
   * Brings playback into line with whether the hero is worth playing to.
   *
   * <p>Called whenever either half of that could have changed: the hero scrolling in or out of the
   * viewport, and the tab being left or come back to. Refusal is an ordinary outcome, not an error —
   * before the film has ever run, the gate's patience gives the photograph back.</p>
   */
  function sync(): void {
    if (onScreen && document.visibilityState === "visible") {
      void video.play().catch(() => undefined);
    } else {
      video.pause();
    }
  }

  /**
   * Answers a pause nobody asked for.
   *
   * <p>Chrome stops video-only media it believes nobody can see, and a modal dialog over the hero is
   * enough — the consent dialog did it in testing, and so did the film's own loop restarting while
   * the window was not being composited. Neither the observer nor a visibility change reports any of
   * that, because neither the geometry nor the tab changed; the only thing that does is the pause
   * itself.</p>
   *
   * <p>Answered <b>once</b>: if the browser refuses again, the film is left where it is rather than
   * fought over in a loop. A successful resume fires `playing`, which arms this again.</p>
   */
  function pausedOnUs(): void {
    if (resuming || !onScreen || document.visibilityState !== "visible") {
      return;
    }

    resuming = true;
    void video.play().catch(() => undefined);
  }

  // This observer only watches; it never reveals. The layer is already showing its first frame when
  // the observer is put on watch, and tools/check-prototype.py accepts it by name on that ground alone.
  const watcher = new IntersectionObserver(entries => {
    onScreen = entries.some(entry => entry.isIntersecting);
    sync();
  }, { threshold: 0.1 });

  /**
   * Reveals the layer on the first decoded frame, starts the film, and puts the observer on watch.
   *
   * <p>`loadeddata` is the moment the element has a frame to paint, which is what makes revealing it
   * honest rather than a guess. All of it waits a frame, until the reveal has been rendered: an
   * observer reports a hidden element as on screen perfectly happily — it measures geometry, not
   * visibility — and playing a hidden film is the one thing that does not work.</p>
   *
   * <p><b>The hero is taken to be on screen rather than waited for, and the observer corrects it.</b>
   * It is the top of the page, so it nearly always is. Leaving the first play to the observer made
   * the film depend on a callback a browser is free to defer, and measurably does: in a window that
   * is not being rendered, neither the observer nor an animation frame ever arrives, and the film
   * never starts. Correcting a rare wrong assumption a frame later is cheaper than missing the
   * common case.</p>
   *
   * <p>Playback is therefore asked for twice — once now and once after the reveal has been painted.
   * `sync` is written to be called whenever anything relevant might have changed, so the second call
   * is not a retry but the same question asked again with the answer possibly different: at the
   * first one the layer may not be composited yet, and a film that is already running takes the
   * repeat as a no-op.</p>
   */
  function firstFrame(): void {
    signals?.show();
    onScreen = true;
    sync();
    window.requestAnimationFrame(() => {
      sync();
      watcher.observe(video);
    });
  }

  /** Tells the gate the film is moving, which is what makes the stop button appear. */
  function moving(): void {
    resuming = false;
    signals?.running();
    signals = null;
  }

  return {
    element: video,

    start(onSignal: HeroFilmSignals): void {
      signals = onSignal;
      video.addEventListener("loadeddata", firstFrame, { once: true });
      // Not once: every start re-arms the answer to an unasked-for pause. The gate is told only the
      // first time, because `signals` is dropped after it.
      video.addEventListener("playing", moving);
      video.addEventListener("pause", pausedOnUs);
      document.addEventListener("visibilitychange", sync);

      const file = document.createElement("source");
      file.src = source;
      file.type = type;
      video.append(file);

      video.preload = "auto";
      video.load();
    },

    stop(): void {
      signals = null;
      onScreen = false;
      watcher.disconnect();
      video.removeEventListener("loadeddata", firstFrame);
      video.removeEventListener("playing", moving);
      video.removeEventListener("pause", pausedOnUs);
      document.removeEventListener("visibilitychange", sync);
      video.pause();

      // Emptying the sources and loading again is what actually aborts a download in progress.
      // Without it a stopped film keeps pulling the rest of the file down.
      video.replaceChildren();
      video.removeAttribute("src");
      video.load();

      video.remove();
    },
  };
}
